import React, { forwardRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTheme } from '../ThemeContext';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Filler, ChartDataLabels);

interface CashFlowChartProps {
  labels: string[];
  operating: number[];
  investing: number[];
  financing: number[];
  net: number[];
  areaLabel?: string;
}

const CashFlowChart = forwardRef<any, CashFlowChartProps>(({ labels, operating, investing, financing, net, areaLabel }, ref) => {
  const { chart } = useTheme();

  const data = {
    labels,
    datasets: [
      { label: 'Operating',  data: operating,  backgroundColor: 'rgba(16, 185, 129, 0.7)', borderColor: '#10b981', borderWidth: 1, borderRadius: 4 },
      { label: 'Investing',  data: investing,  backgroundColor: 'rgba(59, 130, 246, 0.7)', borderColor: '#3b82f6', borderWidth: 1, borderRadius: 4 },
      { label: 'Financing',  data: financing,  backgroundColor: 'rgba(139, 92, 246, 0.7)', borderColor: '#8b5cf6', borderWidth: 1, borderRadius: 4 },
      { label: 'Net Change', data: net,        backgroundColor: 'rgba(6, 182, 212, 0.7)',  borderColor: '#06b6d4', borderWidth: 1, borderRadius: 4 },
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { color: chart.legendColor, font: { family: 'Inter', size: 12, weight: '500' }, usePointStyle: true, pointStyle: 'circle', padding: 16 },
      },
      tooltip: {
        backgroundColor: chart.tooltipBg,
        titleColor: chart.tooltipTitle,
        bodyColor: chart.tooltipBody,
        borderColor: chart.tooltipBorder,
        borderWidth: 1, cornerRadius: 8, padding: 12,
        titleFont: { family: 'Inter', size: 13, weight: '600' },
        bodyFont: { family: 'Inter', size: 12 },
        callbacks: {
          label: (ctx: any) => `${ctx.dataset.label}: Rp ${(ctx.parsed.y / 1e6).toFixed(1)} Jt`,
        },
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        offset: 8,
        font: {
          family: 'Inter',
          size: 11,
          weight: '500',
        },
        color: (context: any) => context.dataset.borderColor,
        formatter: (value: any) => {
          if (value === 0 || value === null) return '';
          return `Rp ${(value / 1e6).toFixed(1)} Jt`;
        },
      },
    },
    scales: {
      x: { ticks: { color: chart.tickColor, font: { family: 'Inter', size: 11 } }, grid: { color: chart.gridColorX } },
      y: { ticks: { color: chart.tickColor, font: { family: 'Inter', size: 11 }, callback: (val: number) => `Rp ${(val / 1e6).toFixed(0)}Jt` }, grid: { color: chart.gridColorY } },
    },
  };

  return (
    <div className="chart-card animate-in" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div className="chart-card-header">
        <div>
          <div className="chart-card-title">💵 Cash Flow</div>
          <div className="chart-card-subtitle">Operating, Investing, Financing per bulan</div>
          {areaLabel && (
            <span style={{
              fontSize: '11px', color: '#f59e0b',
              background: 'rgba(245,158,11,0.1)',
              border: '1px solid rgba(245,158,11,0.3)',
              borderRadius: '4px', padding: '1px 6px',
              marginTop: '4px', display: 'inline-block',
            }}>{areaLabel}</span>
          )}
        </div>
      </div>
      <div className="chart-wrapper bar-chart">
        <Bar ref={ref} data={data} options={options} />
      </div>
    </div>
  );
});

CashFlowChart.displayName = 'CashFlowChart';

export default CashFlowChart;
